import Event from '@mui/icons-material/Event';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import { type Dayjs, dayjs } from 'date';
import { forwardRef, useState } from 'react';

import { type DateRange, DateRangePicker } from '../DateRangePicker/DateRangePicker';
import { Select, type SelectProps } from '../Select/Select';

export type SelectDatePickerProps = Omit<SelectProps, 'onChange' | 'value' | 'defaultValue'> & {
  onChange: ({ from, to }: DateRange) => void;
  options: { label: string; value: Dayjs }[];
  defaultValue: Dayjs | undefined;
  defaultRangeValue?: DateRange;
  anchorDate?: Dayjs;
  rangeSlotProps?: React.ComponentProps<typeof DateRangePicker>['slotProps'];
};

// Localized numeric date token ('L') so the range label follows the active locale.
const DATE_RANGE_FORMAT = 'L';
const DEFAULT_FROM_LABEL = 'From';
const DEFAULT_TO_LABEL = 'To';
const DEFAULT_RANGE_DURATION = { unit: 'week' as const, value: 1 };

const formatDateValue = (date: Dayjs | undefined) => (date ? date.toISOString() : undefined);
const formatDateRangeValue = (rangeValue: DateRange) =>
  `${rangeValue.from.format(DATE_RANGE_FORMAT)} - ${rangeValue.to.format(DATE_RANGE_FORMAT)}`;
const buildRangeFromValue = (value: Dayjs | undefined, anchorDate: Dayjs): DateRange => {
  if (!value) {
    return {
      from: anchorDate,
      to: anchorDate.add(DEFAULT_RANGE_DURATION.value, DEFAULT_RANGE_DURATION.unit),
    };
  }

  const selectedDate = dayjs(value);

  return selectedDate.isBefore(anchorDate)
    ? {
        from: selectedDate,
        to: anchorDate,
      }
    : {
        from: anchorDate,
        to: selectedDate,
      };
};

type SelectDatePickerState = {
  anchorDate: Dayjs | undefined;
  defaultRangeValue: DateRange | undefined;
  defaultValue: Dayjs | undefined;
  rangeValue: DateRange;
  value: Dayjs | undefined | 'range';
};

const areDatesEqual = (currentDate: Dayjs | undefined, nextDate: Dayjs | undefined) =>
  currentDate === nextDate || Boolean(currentDate?.isSame(nextDate));

const areRangeValuesEqual = (currentRange: DateRange | undefined, nextRange: DateRange | undefined) =>
  currentRange === nextRange ||
  (Boolean(currentRange) &&
    Boolean(nextRange) &&
    currentRange?.from.isSame(nextRange?.from) &&
    currentRange?.to.isSame(nextRange?.to));

const haveDefaultValuesChanged = (
  currentState: SelectDatePickerState,
  {
    anchorDate,
    defaultRangeValue,
    defaultValue,
  }: Pick<SelectDatePickerProps, 'anchorDate' | 'defaultRangeValue' | 'defaultValue'>
) =>
  !areDatesEqual(currentState.anchorDate, anchorDate) ||
  !areDatesEqual(currentState.defaultValue, defaultValue) ||
  !areRangeValuesEqual(currentState.defaultRangeValue, defaultRangeValue);

const getStateFromDefaultValues = ({
  anchorDate,
  defaultRangeValue,
  defaultValue,
}: Pick<SelectDatePickerProps, 'anchorDate' | 'defaultRangeValue' | 'defaultValue'>): SelectDatePickerState => {
  const resolvedAnchorDate = anchorDate ?? dayjs();

  return {
    anchorDate,
    defaultRangeValue,
    defaultValue,
    rangeValue: defaultRangeValue ?? buildRangeFromValue(defaultValue, resolvedAnchorDate),
    value: defaultRangeValue ? 'range' : defaultValue,
  };
};

const isOwnRangeDefaultUpdate = (currentState: SelectDatePickerState, defaultRangeValue: DateRange | undefined) =>
  currentState.value === 'range' && areRangeValuesEqual(currentState.rangeValue, defaultRangeValue);

const isOwnDateDefaultUpdate = (currentState: SelectDatePickerState, defaultValue: Dayjs | undefined) =>
  currentState.value !== 'range' && areDatesEqual(currentState.value, defaultValue);

export const SelectDatePicker = forwardRef(function SelectDatePicker(
  { onChange, options, defaultValue, defaultRangeValue, anchorDate, rangeSlotProps, ...rest }: SelectDatePickerProps,
  ref
) {
  const resolvedAnchorDate = anchorDate ?? dayjs();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<SelectDatePickerState>(
    getStateFromDefaultValues({ anchorDate, defaultRangeValue, defaultValue })
  );

  if (haveDefaultValuesChanged(state, { anchorDate, defaultRangeValue, defaultValue })) {
    if (isOwnRangeDefaultUpdate(state, defaultRangeValue) || isOwnDateDefaultUpdate(state, defaultValue)) {
      setState((currentState) => ({
        ...currentState,
        anchorDate,
        defaultRangeValue,
        defaultValue,
      }));
    } else {
      setState(getStateFromDefaultValues({ anchorDate, defaultRangeValue, defaultValue }));
    }
  }

  const { rangeValue, value } = haveDefaultValuesChanged(state, { anchorDate, defaultRangeValue, defaultValue })
    ? getStateFromDefaultValues({ anchorDate, defaultRangeValue, defaultValue })
    : state;

  const selectValue = value === 'range' ? 'range' : formatDateValue(value);
  const pickerDefaultRangeValue =
    value === 'range' ? rangeValue : buildRangeFromValue(defaultValue, resolvedAnchorDate);

  return (
    <Select
      startAdornment={
        <InputAdornment position="start">
          <Event />
        </InputAdornment>
      }
      data-testid="select-date-picker"
      value={value ? selectValue : ''}
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLDivElement;

        const isClickOnSelect = target.getAttribute('role') === 'combobox';
        if (isClickOnSelect) {
          setOpen(true);
          return;
        }

        const isClickOutsideComponent = target.classList.contains('MuiBackdrop-root');
        if (isClickOutsideComponent) {
          setOpen(false);
        }
      }}
      onChange={(event) => {
        event.stopPropagation();
        if (!event.target.value || event.target.value === 'range') {
          return;
        }

        const selectedDate = dayjs(event.target.value);
        const currentAnchor = anchorDate ?? dayjs();
        setState({
          anchorDate,
          defaultRangeValue,
          defaultValue,
          rangeValue: buildRangeFromValue(selectedDate, currentAnchor),
          value: selectedDate,
        });

        if (selectedDate.isBefore(currentAnchor)) {
          onChange({
            from: selectedDate,
            to: currentAnchor,
          });
          setOpen(false);
          return;
        }
        onChange({
          from: currentAnchor,
          to: selectedDate,
        });
        setOpen(false);
      }}
      renderValue={(value) => {
        if (value === 'range') {
          return formatDateRangeValue(rangeValue);
        }
        const selectedOption = options.find((option) => option.value.isSame(value, 'date'));
        return selectedOption ? selectedOption.label : value.toString();
      }}
      defaultValue={formatDateValue(defaultValue)}
      {...rest}
      open={open}
      ref={ref}
    >
      <MenuItem value="range">
        <DateRangePicker
          onChange={({ from, to }) => {
            setState({
              anchorDate,
              defaultRangeValue,
              defaultValue,
              rangeValue: { from, to },
              value: 'range',
            });

            onChange({
              from,
              to,
            });
          }}
          onAccept={() => {
            setOpen(false);
          }}
          defaultValues={pickerDefaultRangeValue}
          slotProps={{
            from: {
              label: rangeSlotProps?.from?.label ?? DEFAULT_FROM_LABEL,
              size: rangeSlotProps?.from?.size,
            },
            separator: rangeSlotProps?.separator,
            to: {
              label: rangeSlotProps?.to?.label ?? DEFAULT_TO_LABEL,
              size: rangeSlotProps?.to?.size,
            },
          }}
        />
      </MenuItem>

      {options.map(({ label, value }) => (
        <MenuItem key={label} value={value.toISOString()}>
          {label}
        </MenuItem>
      ))}
    </Select>
  );
});
